import React, { useState, useEffect, useRef } from 'react';
import renoirLuncheon from '../assets/renoir-luncheon.jpg';
import janSteenDancingCouple from '../assets/jan-steen-dancing-couple.jpg';
import './LandingPage.css';

// Rotating accent colors for donor names in the chat demo and group labels in the list demo
const ACCENT_COLORS = ['#7dd3fc', '#f472b6', '#34d399', '#fbbf24', '#c4b5fd'];
const ACCENT_BG = [
  'rgba(125, 211, 252, 0.15)',
  'rgba(244, 114, 182, 0.15)',
  'rgba(52, 211, 153, 0.15)',
  'rgba(251, 191, 36, 0.15)',
  'rgba(196, 181, 253, 0.15)'
];

const DEMO_NAME_COLORS = {
  "Sarah Chen": ACCENT_COLORS[0],
  "Marcus Thompson": ACCENT_COLORS[1],
  "The Johnsons": ACCENT_COLORS[2],
  "Elena Rodriguez's": ACCENT_COLORS[3]
};

// Demo conversation for the narrative window - grouped into lines (text/bullet/protip/question)
// so it reads like a real, structured advice response rather than a couple of plain sentences
const DEMO_CONVERSATION = {
  userMessage: "who should I follow up with this week?",
  paragraphs: [
    [
      { kind: 'text', segments: [
        { type: 'text', content: "Based on your relationships, here's your priority list this week:" }
      ]},
      { kind: 'bullet', segments: [
        { type: 'name', content: "Sarah Chen" },
        { type: 'text', content: " — ready to move forward on the " },
        { type: 'amount', content: "$125K" },
        { type: 'text', content: " commitment after yesterday's meeting, just needs a follow-up call." }
      ]},
      { kind: 'bullet', segments: [
        { type: 'name', content: "Marcus Thompson" },
        { type: 'text', content: " — gave verbal yes on " },
        { type: 'amount', content: "$50K" },
        { type: 'text', content: ", waiting on paperwork only." }
      ]}
    ],
    [
      { kind: 'text', segments: [
        { type: 'text', content: "Also worth reaching out to:" }
      ]},
      { kind: 'bullet', segments: [
        { type: 'name', content: "The Johnsons" },
        { type: 'text', content: " — coffee Thursday, excited about sponsoring the gala." }
      ]},
      { kind: 'bullet', segments: [
        { type: 'name', content: "Elena Rodriguez's" },
        { type: 'text', content: " VP — she asked you to loop them in before Friday." }
      ]},
      { kind: 'protip', segments: [
        { type: 'text', content: "Sarah and Marcus are basically closed — treat those as paperwork, not persuasion. The real work this week is " },
        { type: 'name', content: "Elena Rodriguez's" },
        { type: 'text', content: " VP." }
      ]},
      { kind: 'question', segments: [
        { type: 'text', content: "Want me to draft the follow-up emails?" }
      ]}
    ]
  ]
};

// Total segments in a paragraph group, used to time how long its reveal animation takes
const countSegments = (group) => group.reduce((sum, line) => sum + line.segments.length, 0);

const renderDemoSegment = (seg, key, delayMs) => {
  const style = { animationDelay: `${delayMs}ms` };
  let className = 'demo-segment';
  if (seg.type === 'name') {
    className += ' demo-name-link';
    style.color = DEMO_NAME_COLORS[seg.content];
  } else if (seg.type === 'amount') {
    className += ' demo-amount';
  }
  return <span key={key} className={className} style={style}>{seg.content}</span>;
};

// Renders a paragraph group (a list of text/bullet/protip/question lines) with a
// continuous stagger delay across all its segments, so longer responses still
// read as one smooth reveal instead of each line popping in all at once.
const renderDemoGroup = (group) => {
  let index = 0;
  return group.map((line, lineIdx) => {
    const startIndex = index;
    index += line.segments.length;
    return (
      <p key={lineIdx} className={`demo-ai-line demo-ai-${line.kind}`}>
        {line.kind === 'bullet' && <span className="demo-bullet-marker">•</span>}
        {line.kind === 'protip' && <strong className="demo-protip-label">Pro tip: </strong>}
        {line.segments.map((seg, i) => renderDemoSegment(seg, i, (startIndex + i) * 80))}
      </p>
    );
  });
};

// Demo list for the organize window
const HERO_CHAT_PHRASE = "Sarah's ready to move forward on the Q4 partnership.";

const DEMO_LIST = {
  prompt: "organize my donors by professional background",
  groups: [
    { name: "Finance & Banking", people: ["Sarah Chen", "The Johnsons", "David Kim"] },
    { name: "Law & Legal", people: ["Marcus Thompson", "Elena Rodriguez"] },
    { name: "Healthcare & Medicine", people: ["Dr. Michael Park", "Linda Wu"] },
    { name: "Technology", people: ["Priya Anand", "Jordan Lee"] },
    { name: "Real Estate", people: ["Frank Ruiz", "Nina Osei", "Ben Turner"] }
  ]
};

function LandingPage({ onEnterApp, onLogin }) {
  const [, setHeaderDark] = useState(false);
  const [, setStoryTextOpacity] = useState(0.3);
  const [storyInView, setStoryInView] = useState(false);

  // Demo conversation state - CSS-driven smooth animations
  const [demoStage, setDemoStage] = useState(-1); // -1=not started, 0=waiting, 1=user shown, 2=AI thinking, 3=AI p1, 4=AI p2, 5=done
  const [demoShowUser, setDemoShowUser] = useState(false);
  const [demoInView, setDemoInView] = useState(false);
  const demoRef = useRef(null);

  // Demo list state
  const [listStage, setListStage] = useState(-1); // -1=not started, 0=waiting, 1=prompt shown, 2+=groups appearing
  const [listShowPrompt, setListShowPrompt] = useState(false);
  const [listVisibleGroups, setListVisibleGroups] = useState([]);

  // Hero chatbar - types a note, then the profile mockup below updates
  const [heroChatStarted, setHeroChatStarted] = useState(false);
  const [heroTypedText, setHeroTypedText] = useState('');
  const [heroProfileUpdated, setHeroProfileUpdated] = useState(false);

  // Mobile carousel state
  const [activeSlide, setActiveSlide] = useState(0);
  const [exitingSlide, setExitingSlide] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  // Book a Demo modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedDemoDay, setSelectedDemoDay] = useState(null);
  const [selectedDemoTime, setSelectedDemoTime] = useState(null);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoError, setDemoError] = useState('');
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hero chatbar - start typing once the hero has had a moment to fade in
  useEffect(() => {
    const timeout = setTimeout(() => setHeroChatStarted(true), 1500);
    return () => clearTimeout(timeout);
  }, []);

  // Hero chatbar - type out the phrase, then reveal the profile update
  useEffect(() => {
    if (!heroChatStarted) return;

    if (heroTypedText.length < HERO_CHAT_PHRASE.length) {
      const timeout = setTimeout(() => {
        setHeroTypedText(HERO_CHAT_PHRASE.slice(0, heroTypedText.length + 1));
      }, 28 + Math.random() * 22);
      return () => clearTimeout(timeout);
    } else if (!heroProfileUpdated) {
      const timeout = setTimeout(() => setHeroProfileUpdated(true), 700);
      return () => clearTimeout(timeout);
    }
  }, [heroChatStarted, heroTypedText, heroProfileUpdated]);

  // Auto-cycle slides on mobile with drop animation
  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      // Set current slide as exiting
      setExitingSlide(activeSlide);

      // After exit animation, show next slide
      setTimeout(() => {
        setActiveSlide(prev => (prev + 1) % 2);
        setExitingSlide(null);
      }, 400); // Wait for exit animation
    }, 4000); // 4 seconds per slide

    return () => clearInterval(interval);
  }, [isMobile, activeSlide]);

  // Touch handlers - disabled on mobile (auto-cycling instead)
  const handleTouchStart = (e) => {
    if (isMobile) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (isMobile) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (isMobile) return;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (diff > threshold && activeSlide < 1) {
      setActiveSlide(prev => prev + 1);
    } else if (diff < -threshold && activeSlide > 0) {
      setActiveSlide(prev => prev - 1);
    }
  };

  // Detect when header is over dark section + story text opacity
  useEffect(() => {
    const handleScroll = () => {
      const story = document.querySelector('.landing-story');
      if (story) {
        const rect = story.getBoundingClientRect();
        // Header is at top, check if story section top is above ~80px (header height area)
        setHeaderDark(rect.top < 80 && rect.bottom > 0);

        // Calculate story text opacity based on scroll
        // When section top is at viewport bottom, opacity = 0.3
        // When section is fully scrolled into view, opacity = 1
        const viewportHeight = window.innerHeight;
        const sectionVisibility = 1 - (rect.top / viewportHeight);
        const opacity = Math.min(1, Math.max(0.3, 0.3 + (sectionVisibility * 0.7)));
        setStoryTextOpacity(opacity);

        // Trigger animations when section is 30% visible
        if (sectionVisibility > 0.3 && !storyInView) {
          setStoryInView(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [storyInView]);

  // Intersection Observer - start animations when demo windows come into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !demoInView) {
            setDemoInView(true);
            // Start both animations
            setDemoStage(0);
            setListStage(0);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (demoRef.current) {
      observer.observe(demoRef.current);
    }

    return () => observer.disconnect();
  }, [demoInView]);

  // Demo conversation animation - CSS-driven smooth timing (plays once)
  useEffect(() => {
    // Don't run if not started yet
    if (demoStage < 0) return;

    const p1Duration = countSegments(DEMO_CONVERSATION.paragraphs[0]) * 80 + 200;
    const p2Duration = countSegments(DEMO_CONVERSATION.paragraphs[1]) * 80 + 200;

    // Stage 0: Wait, then bloom user message
    if (demoStage === 0) {
      const timeout = setTimeout(() => {
        setDemoShowUser(true);
        setDemoStage(1);
      }, 300);
      return () => clearTimeout(timeout);
    }

    // Stage 1: User message shown, wait then show thinking
    if (demoStage === 1) {
      const timeout = setTimeout(() => setDemoStage(2), 300);
      return () => clearTimeout(timeout);
    }

    // Stage 2: AI thinking, then start first paragraph
    if (demoStage === 2) {
      const timeout = setTimeout(() => setDemoStage(3), 400);
      return () => clearTimeout(timeout);
    }

    // Stage 3: First paragraph animating
    if (demoStage === 3) {
      const timeout = setTimeout(() => setDemoStage(4), p1Duration);
      return () => clearTimeout(timeout);
    }

    // Stage 4: Second paragraph animating
    if (demoStage === 4) {
      const timeout = setTimeout(() => setDemoStage(5), p2Duration);
      return () => clearTimeout(timeout);
    }

    // Stage 5: Done - stay here (no restart)
  }, [demoStage]);

  // Demo list animation - faster timing (plays once)
  useEffect(() => {
    // Don't run if not started yet
    if (listStage < 0) return;

    const groups = DEMO_LIST.groups;

    // Stage 0: Wait, then show prompt
    if (listStage === 0) {
      const timeout = setTimeout(() => {
        setListShowPrompt(true);
        setListStage(1);
      }, 400);
      return () => clearTimeout(timeout);
    }

    // Stage 1: Prompt shown, start showing groups
    if (listStage === 1) {
      const timeout = setTimeout(() => setListStage(2), 300);
      return () => clearTimeout(timeout);
    }

    // Stages 2 through (2 + groups.length - 1): show each group one by one
    if (listStage >= 2 && listStage <= 1 + groups.length) {
      const groupIndex = listStage - 2;
      if (groupIndex < groups.length && !listVisibleGroups.includes(groupIndex)) {
        setListVisibleGroups(prev => [...prev, groupIndex]);
      }
      const timeout = setTimeout(() => setListStage(listStage + 1), 250);
      return () => clearTimeout(timeout);
    }

    // Done - stay here (no restart)
  }, [listStage, listVisibleGroups]);

  // Contact form handlers
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactMessage.trim() || contactSubmitting) return;

    setContactSubmitting(true);
    setContactError('');

    try {
      const response = await fetch('/api/landing-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: contactEmail.trim() || null,
          message: contactMessage.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setContactError(data.error || 'Something went wrong. Please try again.');
      } else {
        setContactSuccess(true);
        setContactEmail('');
        setContactMessage('');
        setTimeout(() => {
          setShowContactModal(false);
          setContactSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Contact submit error:', error);
      setContactError('Something went wrong. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setContactError('');
    setContactSuccess(false);
  };

  // Next 5 upcoming weekdays, generated fresh from today - not hardcoded stale dates
  const getUpcomingWeekdays = () => {
    const days = [];
    const cursor = new Date();
    while (days.length < 5) {
      cursor.setDate(cursor.getDate() + 1);
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) {
        days.push({
          label: cursor.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          value: cursor.toDateString()
        });
      }
    }
    return days;
  };
  const demoDays = getUpcomingWeekdays();
  const demoTimeSlots = ['9:00 AM', '11:00 AM', '1:30 PM', '4:00 PM'];

  const closeDemoModal = () => {
    setShowDemoModal(false);
    setSelectedDemoDay(null);
    setSelectedDemoTime(null);
    setDemoError('');
    setDemoSuccess(false);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (demoSubmitting) return;

    if (!selectedDemoDay || !selectedDemoTime) {
      setDemoError('Pick a day and a time first.');
      return;
    }
    if (!demoEmail.trim() || !demoEmail.includes('@')) {
      setDemoError('Enter a valid email so we can confirm your time.');
      return;
    }

    setDemoSubmitting(true);
    setDemoError('');

    try {
      const response = await fetch('/api/book-demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: demoEmail.trim(),
          day: selectedDemoDay,
          time: selectedDemoTime
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setDemoError(data.error || 'Something went wrong. Please try again.');
      } else {
        setDemoSuccess(true);
        setDemoEmail('');
        setTimeout(() => {
          closeDemoModal();
        }, 2500);
      }
    } catch (error) {
      console.error('Demo submit error:', error);
      setDemoError('Something went wrong. Please try again.');
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Floating Header */}
      <header className="landing-header dark">
        <div className="landing-header-inner">
          <div className="landing-header-logo">
            <span className="landing-header-logo-text">knowroom</span>
          </div>
          <nav className="landing-header-nav">
            <button className="landing-nav-link" onClick={onLogin || onEnterApp}>Login</button>
            <button className="landing-nav-signup" onClick={() => setShowDemoModal(true)}>Book a Demo</button>
          </nav>
        </div>
      </header>

      {/* Single continuous gradient wrapper spanning hero through Story - no seams between sections */}
      <div className="landing-gradient-sections">

      {/* Main content */}
      <main className="landing-main" style={{ '--hero-bg-image': `url(${janSteenDancingCouple})` }}>
        <h1 className="landing-headline">
          <span className="landing-line">
            Relationships are <span className="accent">stories,</span>
          </span>
          <span className="landing-line">
            not spreadsheets.
          </span>
        </h1>

        <p className="landing-subheadline">
          Describe your relationships with donors, clients, anyone — and Knowroom turns those stories into insight.
        </p>

        <button className="landing-cta-pill" onClick={() => setShowDemoModal(true)}>
          Book a Demo
        </button>

        {/* Chatbar - types a note about Sarah Chen, then the profile below updates */}
        <div className="landing-chatbar" onClick={onEnterApp}>
          <div className="landing-chatbar-input">
            <span>{heroTypedText}</span>
            <span className="landing-chatbar-cursor">|</span>
          </div>
          <div className={`landing-chatbar-send ${heroProfileUpdated ? 'sent' : ''}`}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Profile view preview - mirrors the real product's Story / Key Details / Timeline layout */}
        <div className="landing-mockup" onClick={onEnterApp}>
          <div className="landing-mockup-header">
            <div className="landing-mockup-person">
              <div className="landing-mockup-avatar">SC</div>
              <div>
                <div className="landing-mockup-person-name">Sarah Chen</div>
                <div className="landing-mockup-person-role">VP of Operations, Acme Corp</div>
              </div>
            </div>
            <span className={`landing-mockup-live ${heroProfileUpdated ? 'updated' : ''}`}>
              <span className="landing-mockup-dot"></span>
              {heroProfileUpdated ? 'rebuilt just now' : 'live'}
            </span>
          </div>
          <div className="landing-mockup-columns">
            <div className="landing-mockup-col">
              <div className="landing-mockup-label reveal" style={{ animationDelay: '0.1s' }}>The Story</div>
              <p className="landing-mockup-story reveal" style={{ animationDelay: '0.2s' }}>
                Called to discuss Q4 partnership expansion — very interested in the enterprise tier. You met at the conference in Austin, where she mentioned her team is growing to 50 people. Introduced by David Kim, who she worked with at a previous company.
              </p>
              <div className="landing-mockup-label reveal" style={{ animationDelay: '0.35s' }}>Key Details</div>
              <div className="landing-mockup-tags reveal" style={{ animationDelay: '0.45s' }}>
                <span className="landing-mockup-tag">$125K pipeline</span>
                <span className="landing-mockup-tag">VP of Operations</span>
                <span className="landing-mockup-tag strong">Strong relationship</span>
              </div>
            </div>
            <div className="landing-mockup-col">
              <div className="landing-mockup-label reveal" style={{ animationDelay: '0.1s' }}>Timeline</div>
              <div className="landing-mockup-timeline">
                <div className={`landing-mockup-entry new-entry ${heroProfileUpdated ? 'reveal' : 'new-entry-hidden'}`}>
                  <span className="landing-mockup-entry-dot new"></span>
                  <div>
                    <span className="landing-mockup-entry-date">Just now</span>
                    <p>{HERO_CHAT_PHRASE}</p>
                  </div>
                </div>
                <div className="landing-mockup-entry reveal" style={{ animationDelay: '0.25s' }}>
                  <span className="landing-mockup-entry-dot"></span>
                  <div>
                    <span className="landing-mockup-entry-date">Today</span>
                    <p>Called to discuss Q4 partnership expansion.</p>
                  </div>
                </div>
                <div className="landing-mockup-entry reveal" style={{ animationDelay: '0.4s' }}>
                  <span className="landing-mockup-entry-dot"></span>
                  <div>
                    <span className="landing-mockup-entry-date">Last week</span>
                    <p>Met at the conference in Austin.</p>
                  </div>
                </div>
                <div className="landing-mockup-entry reveal" style={{ animationDelay: '0.55s' }}>
                  <span className="landing-mockup-entry-dot"></span>
                  <div>
                    <span className="landing-mockup-entry-date">2 weeks ago</span>
                    <p>Intro from David Kim.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>

      {/* Scrolling marquee of what knowroom replaces */}
      <section className="landing-marquee-section">
        <span className="landing-marquee-heading">We save you time by:</span>
        <div className="landing-marquee-rows">
          <div className="landing-marquee">
            <div className="landing-marquee-track">
              {['Removing data entry', 'Letting your team see the same narrative', 'Pulling data from natural language'].concat(['Removing data entry', 'Letting your team see the same narrative', 'Pulling data from natural language']).map((phrase, i) => (
                <span className="landing-marquee-item" key={`a-${i}`}>{phrase}</span>
              ))}
            </div>
          </div>
          <div className="landing-marquee">
            <div className="landing-marquee-track landing-marquee-track-reverse">
              {['Updating schedules automatically', 'Providing insight into relationships'].concat(['Updating schedules automatically', 'Providing insight into relationships']).map((phrase, i) => (
                <span className="landing-marquee-item" key={`b-${i}`}>{phrase}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence section */}
      <section className="landing-intelligence-wrapper">
        <div className="landing-intelligence">
          <h2 className="landing-intelligence-text">
            The data is in the <span className="rainbow">story.</span>
          </h2>
        </div>

        {/* Demo Windows Container */}
        <div
          ref={demoRef}
          className="landing-demo-windows"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="landing-demo-track" style={{ transform: isMobile ? 'none' : `translateX(-${activeSlide * 100}%)` }}>
          {/* Narrative Feed Window - LEFT */}
          <div className={`landing-demo-window demo-narrative-window ${activeSlide === 0 ? 'active' : ''} ${exitingSlide === 0 ? 'exiting' : ''}`}>
            <div className="demo-window-content">
              <div className="demo-narrative">
                {/* User message - blooms in at top */}
                {demoShowUser && (
                  <div className="demo-feed-item user bloom">
                    <div className="demo-feed-content user-content">
                      <p>{DEMO_CONVERSATION.userMessage}</p>
                    </div>
                  </div>
                )}

                {/* AI response area - scrolls below */}
                <div className="demo-ai-response-area">
                  {/* AI thinking dots */}
                  {demoStage === 2 && (
                    <div className="demo-thinking-dots">
                      <span></span><span></span><span></span>
                    </div>
                  )}

                  {/* AI response - CSS-driven smooth reveal */}
                  {demoStage >= 3 && (
                    <div className="demo-feed-item ai">
                      <div className="demo-feed-content ai-content">
                        {renderDemoGroup(DEMO_CONVERSATION.paragraphs[0])}
                        {demoStage >= 4 && renderDemoGroup(DEMO_CONVERSATION.paragraphs[1])}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* List Organization Window - RIGHT */}
          <div className={`landing-demo-window demo-list-window ${activeSlide === 1 ? 'active' : ''} ${exitingSlide === 1 ? 'exiting' : ''}`}>
            <div className="demo-window-content">
              {/* Floating chat bar */}
              {listShowPrompt && (
                <div className="demo-list-chatbar bloom">
                  <span className="demo-list-prompt">{DEMO_LIST.prompt}</span>
                  <div className="demo-list-send">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  </div>
                </div>
              )}

              {/* List groups forming */}
              <div className="demo-list-groups">
                {DEMO_LIST.groups.map((group, groupIndex) => {
                  const accentColor = ACCENT_COLORS[groupIndex % ACCENT_COLORS.length];
                  const accentBg = ACCENT_BG[groupIndex % ACCENT_BG.length];
                  return listVisibleGroups.includes(groupIndex) && (
                    <div key={groupIndex} className="demo-list-group">
                      <h4 className="demo-list-group-name" style={{ color: accentColor }}>{group.name}</h4>
                      <div className="demo-list-people">
                        {group.people.map((person, personIndex) => (
                          <span
                            key={personIndex}
                            className="demo-list-person"
                            style={{
                              animationDelay: `${personIndex * 0.1}s`,
                              background: accentBg,
                              color: accentColor
                            }}
                          >
                            {person}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </div>

          {/* Carousel Dots - mobile only */}
          <div className="landing-demo-dots">
            {[0, 1].map((index) => (
              <button
                key={index}
                className={`landing-demo-dot ${activeSlide === index ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Story section with footer */}
      <section className="landing-story" style={{ '--story-bg-image': `url(${renoirLuncheon})` }}>
        <div className={`landing-story-text-wrapper ${storyInView ? 'in-view' : ''}`}>
          <h2 className="landing-story-headline">
            The ease of <span className="glow-word">conversation.</span>
          </h2>
          <p className="landing-story-subtext">
            Knowroom liberates you from your <span className="glow-word">CRM.</span> It pulls data from the <span className="glow-word">story</span> of relationships, without manual data entry.
          </p>
          <button className="landing-demo-cta" onClick={() => setShowDemoModal(true)}>
            Book a Demo
          </button>
        </div>

        <footer className="landing-footer">
          <div className="landing-footer-left">
            <span className="landing-footer-logo">knowroom</span>
            <span className="landing-footer-copyright">&copy; {new Date().getFullYear()} Knowroom Inc.</span>
          </div>
          <div className="landing-footer-links">
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="landing-footer-link">Terms of Service</a>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="landing-footer-link">Privacy Policy</a>
          </div>
        </footer>
      </section>

      </div>

      {/* Contact Us Modal */}
      {showContactModal && (
        <div className="landing-contact-overlay" onClick={closeContactModal}>
          <div className="landing-contact-modal" onClick={(e) => e.stopPropagation()}>
            <button className="landing-contact-close" onClick={closeContactModal}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="landing-contact-title">Get in touch</h2>
            <p className="landing-contact-subtitle">Send us a message and we'll get back to you soon.</p>

            {contactSuccess ? (
              <div className="landing-contact-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Message sent! We'll be in touch.</span>
              </div>
            ) : (
              <form className="landing-contact-form" onSubmit={handleContactSubmit}>
                <input
                  type="email"
                  className="landing-contact-input"
                  placeholder="Your email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                />
                <textarea
                  className="landing-contact-textarea"
                  placeholder="How can we help?"
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  rows={4}
                  required
                />
                {contactError && <p className="landing-contact-error">{contactError}</p>}
                <button type="submit" className="landing-contact-submit" disabled={contactSubmitting}>
                  {contactSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Book a Demo Modal */}
      {showDemoModal && (
        <div className="landing-contact-overlay" onClick={closeDemoModal}>
          <div className="landing-contact-modal landing-demo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="landing-contact-close" onClick={closeDemoModal}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="landing-contact-title">Book a Demo</h2>
            <p className="landing-contact-subtitle">Pick a time that works — we'll confirm by email.</p>

            {demoSuccess ? (
              <div className="landing-contact-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Got it! We'll email you to confirm {selectedDemoDay} at {selectedDemoTime}.</span>
              </div>
            ) : (
              <form className="landing-contact-form" onSubmit={handleDemoSubmit}>
                <div className="landing-demo-days">
                  {demoDays.map((day) => (
                    <button
                      type="button"
                      key={day.value}
                      className={`landing-demo-day ${selectedDemoDay === day.value ? 'selected' : ''}`}
                      onClick={() => setSelectedDemoDay(day.value)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>

                {selectedDemoDay && (
                  <div className="landing-demo-times">
                    {demoTimeSlots.map((time) => (
                      <button
                        type="button"
                        key={time}
                        className={`landing-demo-time ${selectedDemoTime === time ? 'selected' : ''}`}
                        onClick={() => setSelectedDemoTime(time)}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}

                <input
                  type="email"
                  className="landing-contact-input"
                  placeholder="Your email"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                />
                {demoError && <p className="landing-contact-error">{demoError}</p>}
                <button
                  type="submit"
                  className="landing-contact-submit"
                  disabled={demoSubmitting || !selectedDemoDay || !selectedDemoTime}
                >
                  {demoSubmitting ? 'Requesting...' : 'Request this time'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;

